import * as Sentry from "@sentry/react-native";

import EnvironmentVariablesLoader from "@/utils/environment";
import { type ApiResponseWithError, NetworkError } from "@/schemas/api/error";
import { type PaginatedRequest } from "@/schemas/api/pagination";

export abstract class ApiBase {
  protected baseUrl: string;
  protected servicePath: string;
  protected clientId: string;

  public constructor(subPath: string) {
    const { baseApiUrl, apiClientId } =
      EnvironmentVariablesLoader.getEnvironmentVariables();
    this.baseUrl = baseApiUrl;
    this.clientId = apiClientId;
    this.servicePath = subPath;
  }

  /**
   * Each derived class must implement how it builds fetch options
   * (e.g., setting headers, body, etc.).
   */
  protected abstract buildFetchOptions(
    method: string,
    data?: unknown,
  ): RequestInit;

  /**
   * Optionally override if you need special handling (like token refresh)
   * before actually sending the request.
   */
  protected async doRequest<T>(
    subUrl: string,
    method: string,
    data?: unknown,
  ): Promise<T> {
    const fullUrl = `${this.baseUrl}${this.servicePath}${subUrl}`;
    const options = this.buildFetchOptions(method, data);

    try {
      const response = await fetch(fullUrl, options);
      return this.returnOrRaise<T>(fullUrl, response);
    } catch (error) {
      if (
        error instanceof Error &&
        (error.message === "Network request failed" ||
          error.message === "Failed to fetch")
      ) {
        throw new NetworkError("Network connection failed");
      } else {
        throw error;
      }
    }
  }

  /**
   * A common error-handling function.
   * Can be overridden for specialized handling (e.g., Sentry contexts or custom error objects).
   */
  protected async returnOrRaise<T>(
    url: string,
    response: Response,
  ): Promise<T> {
    if (response.ok) {
      const responseData = await response.json();
      // If your API encloses data under `responseData.data`, handle that
      return responseData.data ?? responseData;
    } else {
      let rawData: any; // eslint-disable-line @typescript-eslint/no-explicit-any
      let errorData: ApiResponseWithError;
      let error: Error;

      // Step 1: Check content type and parse accordingly
      const contentType = response.headers.get("content-type");

      try {
        if (contentType && contentType.includes("application/json")) {
          // Parse as JSON
          rawData = await response.json();
          if ("error" in rawData && typeof rawData.error === "object") {
            errorData = rawData as ApiResponseWithError;
            error = new Error(`${errorData.error.message}`, {
              cause: errorData.error,
            });
          } else {
            // JSON response but not in expected error format
            throw new Error("Invalid error response format");
          }
        } else {
          // Handle non-JSON responses (plain text, HTML, etc.)
          const textResponse = await response.text();

          // Generate unique error ID for tracing
          const errorId = `ERR-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

          // Create user-friendly error message without exposing backend details
          const userMessage = `Something went wrong. Please try again. (Error ID: ${errorId})`;

          error = new Error(userMessage, {
            cause: {
              statusCode: response.status,
              statusText: response.statusText,
              body: textResponse,
              errorId,
            },
          });

          // Store error ID in rawData for Sentry logging
          rawData = {
            errorId,
            statusCode: response.status,
            // Technical details kept internal, not exposed to user
          };
        }
      } catch (parseError) {
        // Fallback for any parsing errors
        error = new Error(`Failed to parse response from ${url}`, {
          cause: {
            originalError: parseError,
            statusCode: response.status,
            statusText: response.statusText,
          },
        });
        rawData = { parseError: String(parseError) };
      }

      this.raiseSentryException(response, rawData, error);
      throw error;
    }
  }

  protected raiseSentryException(
    response: Response,
    errorData: object,
    error: Error,
  ): void {
    Sentry.withScope((scope) => {
      // Extract error ID from errorData if available for tagging
      const errorId =
        "errorId" in errorData
          ? (errorData as { errorId: string }).errorId
          : undefined;

      if (errorId) {
        scope.setTag("error_id", errorId);
      }

      scope.setContext("response", {
        errorData,
        headers: Object.fromEntries(response.headers),
        ok: response.ok,
        redirected: response.redirected,
        statusCode: response.status,
        statusText: response.statusText,
        type: response.type,
        url: response.url,
      });

      // Include full technical details in cause for debugging
      if (error.cause) {
        scope.setContext("error_cause", error.cause as Record<string, unknown>);
      }

      Sentry.captureException(error);
    });
  }

  // Convenience methods that rely on doRequest:
  public async get<T>(subUrl: string): Promise<T> {
    return this.doRequest<T>(subUrl, "GET");
  }

  public async post<T>(subUrl: string, data: unknown): Promise<T> {
    return this.doRequest<T>(subUrl, "POST", data);
  }

  public async patch<T>(subUrl: string, data: unknown): Promise<T> {
    return this.doRequest<T>(subUrl, "PATCH", data);
  }

  public async delete<T>(subUrl: string): Promise<T> {
    return this.doRequest<T>(subUrl, "DELETE");
  }

  public buildPaginateQueryStr(page: PaginatedRequest): string {
    const params = new URLSearchParams();

    if (page.limit) {
      params.append("limit", String(page.limit));
    }
    if (page.next) {
      params.append("next", page.next);
    }

    const queryString = params.toString();
    return queryString ? `?${queryString}` : "";
  }
}
